import React, { useEffect, useState } from 'react';

const DEFAULTS = {
  speech_bubble: "Woof! There's nothing here...",
  heading: "Page Not Found",
  description: "The page you're looking for doesn't exist or has been moved.",
  button1_label: "Go Home",
  button1_path: "/",
  button1_icon: "\ud83c\udfe0",
  button2_label: "Translate",
  button2_path: "/translation",
  button2_icon: "\ud83c\udf10",
  footer_text: "DarijaPro 2026",
  image_url: ""
};

const NotFoundPage = ({ isDarkMode }) => {
  const [content, setContent] = useState(DEFAULTS);
  const [checkingRedirect, setCheckingRedirect] = useState(true);

  useEffect(() => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const currentPath = window.location.pathname;

    // 1. Check for an admin-configured redirect FIRST. If one exists, send the visitor there.
    //    Only log/render the 404 UI if no redirect is configured.
    const checkRedirectThenLog = async () => {
      try {
        const r = await fetch(`${backendUrl}/api/seo/check-redirect?path=${encodeURIComponent(currentPath)}`);
        if (r.ok) {
          const data = await r.json();
          const target = data?.to_url;
          if (data?.redirect && target && target !== currentPath) {
            // External URL → full navigation. Internal path → same.
            window.location.replace(target);
            return; // stop — never render the 404 UI
          }
        }
      } catch (e) { /* fall through to 404 */ }

      // No redirect → log the 404 and reveal the UI.
      setCheckingRedirect(false);
      try {
        await fetch(`${backendUrl}/api/seo/log-404`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ uri: currentPath })
        });
      } catch (e) { /* non-critical */ }
    };

    // Load 404 page settings (parallel — independent of redirect check)
    const loadSettings = async () => {
      try {
        const res = await fetch(`${backendUrl}/api/seo/404-page-settings`);
        if (res.ok) {
          const data = await res.json();
          setContent(prev => ({ ...prev, ...data }));
        }
      } catch (e) { /* use defaults */ }
    };

    loadSettings();
    checkRedirectThenLog();
  }, []);

  // Brief blank state while we check for a redirect — prevents the 404 UI flashing
  // before window.location.replace fires.
  if (checkingRedirect) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        isDarkMode ? 'bg-gray-900' : 'bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50'
      }`}>
        <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>...</div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen flex items-center justify-center p-4 ${
      isDarkMode
        ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900'
        : 'bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50'
    }`}>
      <div className="max-w-lg w-full text-center">

        {/* Speech bubble */}
        <div className="relative inline-block mb-2">
          <div className={`px-6 py-3 rounded-2xl shadow-lg ${
            isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-white text-gray-700'
          }`}>
            <p className="text-lg font-medium">{content.speech_bubble}</p>
          </div>
          {/* Triangle pointing down to dog */}
          <div className={`w-4 h-4 rotate-45 mx-auto ${
            isDarkMode ? 'bg-gray-700' : 'bg-white'
          }`} style={{ marginTop: '-10px' }} />
        </div>

        {/* Custom Image (uploaded via SEO > 404 Page tab) */}
        {content.image_url && (
          <div className="mb-4">
            <img src={content.image_url} alt="404" className="w-48 h-48 mx-auto object-contain" />
          </div>
        )}

        {/* 404 heading */}
        <h1 className={`text-8xl font-black mb-2 ${
          isDarkMode
            ? 'text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-500'
            : 'text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-orange-600'
        }`}>
          404
        </h1>
        <h2 className={`text-2xl font-bold mb-2 ${
          isDarkMode ? 'text-white' : 'text-gray-900'
        }`}>
          {content.heading}
        </h2>
        <p className={`text-sm mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          {content.description}
        </p>

        {/* Action buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center mb-6">
          <button
            onClick={() => window.location.href = content.button1_path}
            className="px-8 py-3 rounded-xl font-bold text-white bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-500 hover:to-purple-600 transition-all transform hover:scale-105 shadow-lg"
          >
            {content.button1_icon} {content.button1_label}
          </button>
          <button
            onClick={() => window.location.href = content.button2_path}
            className="px-8 py-3 rounded-xl font-bold text-white bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-400 hover:to-amber-400 transition-all transform hover:scale-105 shadow-lg"
          >
            {content.button2_icon} {content.button2_label}
          </button>
        </div>

        {/* Paw prints decoration */}
        <div className={`flex justify-center gap-3 mb-4 ${isDarkMode ? 'opacity-20' : 'opacity-15'}`}>
          <span className="text-2xl">🐾</span>
          <span className="text-2xl">🐾</span>
          <span className="text-2xl">🐾</span>
        </div>

        {/* Footer */}
        <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          {content.footer_text}
        </p>
      </div>
    </div>
  );
};

export default NotFoundPage;
